import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import mapVehicleIcon from "../../assets/icons/mapvehicleicon.svg";
import { GoogleMap, Marker, Polyline,Polygon } from "@react-google-maps/api";
import { makeGetRequestToUrl } from "../commonFunc.jsx";
import config from "../../config/config";
import Navbar from "../navbar/PublicNavabar.jsx";
import {
  Box,
  Typography,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import vehicleTypeCar from '../../assets/images/vehicleTypeCar.svg';
import vehicletypeAuto from '../../assets/images/vehicletypeAuto.svg';
import vehicleTypeBike from '../../assets/images/vehicleTypeBike.svg';
import altProfile from '../../assets/images/altProfile.png';
import Pickupicon from '../../assets/icons/PickupIcon.svg';
import Dropicon from '../../assets/icons/DropIcon.svg';
import StarIcon from "@mui/icons-material/Star";
import pickupIcon from '../../assets/icons/PickupIcon.svg';
import dropIcon from '../../assets/icons/DropIcon.svg';
const POLL_INTERVAL = 10000; // 10 seconds
const HERE_ROUTING_URL = "https://router.hereapi.com/v8/routes";

const containerStyle = {
  width: "100%",
  height: "100vh",
};

// ──────────────────────────────────────────────────────────────────────────────
// HERE Flexible Polyline decoder
//
// HERE Routing v8 returns route geometry as a compact base-64ish string
// (e.g. "BGu-xq2Bg0vhzEx0C..."). Google's <Polyline> wants {lat,lng}[], so
// we decode it ourselves. This is adapted from HERE's reference JS impl
// (https://github.com/heremaps/flexible-polyline), with Math.pow used in
// place of bit shifts so accumulated values can exceed 2^31 safely.
// ──────────────────────────────────────────────────────────────────────────────
const FP_DECODING_TABLE = [
  62, -1, -1, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1, -1, -1, -1,
  -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  21, 22, 23, 24, 25, -1, -1, -1, -1, 63, -1, 26, 27, 28, 29, 30, 31, 32, 33,
  34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
];

const decodeFlexiblePolyline = (encoded) => {
  let index = 0;

  const readVarint = () => {
    let value = 0;
    let shift = 0;
    let chunk;
    do {
      chunk = FP_DECODING_TABLE[encoded.charCodeAt(index++) - 45];
      value += (chunk & 0x1f) * Math.pow(2, shift);
      shift += 5;
    } while (chunk & 0x20);
    return value;
  };

  // ZigZag → signed
  const zigzagDecode = (v) => (v % 2 === 1 ? -((v + 1) / 2) : v / 2);

  const version = readVarint();
  if (version !== 1) {
    throw new Error("Unsupported flexible-polyline version: " + version);
  }

  const headerInfo = readVarint();
  const precision = headerInfo & 15;
  // eslint-disable-next-line no-bitwise
  const thirdDim = (headerInfo >> 4) & 7;
  const factorDegree = Math.pow(10, precision);

  let lat = 0;
  let lng = 0;
  const points = [];

  while (index < encoded.length) {
    lat += zigzagDecode(readVarint()) / factorDegree;
    lng += zigzagDecode(readVarint()) / factorDegree;
    if (thirdDim) {
      // We don't care about elevation/altitude — consume and discard.
      readVarint();
    }
    points.push({ lat, lng });
  }

  return points;
};

// Expand a ring outward by ~`meters` so HERE keeps the route clear of the edge.
const bufferRing = (ring, meters = 60) => {
  // rough centroid
  const cx = ring.reduce((s, p) => s + p.lat, 0) / ring.length;
  const cy = ring.reduce((s, p) => s + p.lng, 0) / ring.length;
  const dLat = meters / 111320; // deg per metre (lat)
  return ring.map((p) => {
    const dLng = meters / (111320 * Math.cos((cx * Math.PI) / 180));
    return {
      lat: p.lat + (p.lat >= cx ? dLat : -dLat),
      lng: p.lng + (p.lng >= cy ? dLng : -dLng),
    };
  });
};

const buildAvoidAreasParam = (polygons) => {
  if (!Array.isArray(polygons) || polygons.length === 0) return null;
  const parts = [];
  for (const zone of polygons) {
    if (!zone?.geofence) continue;
    for (const ring of zone.geofence) {
      if (!Array.isArray(ring) || ring.length < 3) continue;
      let vertices = ring;
      if (vertices.length > 20) {
        const step = Math.ceil(vertices.length / 20);
        vertices = vertices.filter((_, i) => i % step === 0).slice(0, 20);
      }
      const coords = vertices.map((pt) => `${pt.x},${pt.y}`).join(";");
      parts.push(`polygon:${coords}`);
    }
  }
  return parts.length ? parts.join("|") : null;
};
const buildDangerAvoidParam = (zones) => {
  if (!Array.isArray(zones) || zones.length === 0) return null;
  const parts = [];
  for (const zone of zones) {
    if (!zone?.geofence) continue;
    for (const ring of zone.geofence) {
      if (!Array.isArray(ring) || ring.length < 3) continue;
      let vertices = bufferRing(ring, 60);   // inflate the danger zone
      if (vertices.length > 20) {
        const step = Math.ceil(vertices.length / 20);
        vertices = vertices.filter((_, i) => i % step === 0).slice(0, 20);
      }
      // HERE wants an OPEN ring — drop the repeated closing point
      const first = vertices[0];
      const last = vertices[vertices.length - 1];
      if (last.lat === first.lat && last.lng === first.lng) {
        vertices = vertices.slice(0, -1);
      }
      const coords = vertices.map((pt) => `${pt.lat},${pt.lng}`).join(";");
      parts.push(`polygon:${coords}`);
    }
  }
  return parts.length ? parts.join("|") : null;
};

// HERE only gives us a duration in seconds. Google was returning a
// pre-formatted string like "12 mins"; reproduce that shape so the UI
// looks identical regardless of which provider answered.
const formatDurationText = (sec) => {
  const mins = Math.max(1, Math.round(sec / 60));
  if (mins < 60) return mins === 1 ? "1 min" : `${mins} mins`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return h === 1 ? "1 hr" : `${h} hrs`;
  return `${h} hr ${m} mins`;
};

const getNextDummyLocation = (lat, lng) => ({
  lat: lat + 0.00012,
  lng: lng + 0.00008,
});

const MapViewPublic = ({ rideDetails, isLoaded, data, hereApiKey }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');
  const serverUrl = config.serverUrl;
  const [pickupLatLng, setPickupLatLng] = useState(null);
  const [dropLatLng, setDropLatLng] = useState(null);
  const [dangerZones, setDangerZones] = useState([]);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const isMobile = window.innerWidth < 600;
  const [roadPath, setRoadPath] = useState([]);
  const [currentLatLng, setCurrentLatLng] = useState(null);
  const [previousLatLng, setPreviousLatLng] = useState(null);
  const [taxiCurrentLatLng, setTaxiCurrentLatLng] = useState(null);
  const [ridewaypoints, setRideWaypoints] = useState([]);
  const [taxiPreviousLatLng, setTaxiPreviousLatLng] = useState(null);
  const [taxiMarker, setTaxiMarker] = useState(null);
  const followTaxiRef = useRef(true);
  const [etaText, setEtaText] = useState("");
  const [dropTimeText, setDropTimeText] = useState("");
  const [rideEndCheck, setRideEndCheck] = useState(false);
  const [driverToDropPath, setDriverToDropPath] = useState([]);
  const logo = data?.brand_logo;
  const avoidPolygons = data?.avoid_polygons;

  const lastEtaUpdateRef = useRef(0);
  const infoWindowRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const routeReqIdRef = useRef(0);
  const [driverLocation, setDriverlocation] = useState([]);

  const useHereRouting = rideDetails?.is_safest_route === 1 && !!hereApiKey;

  const routeBetween = useCallback(
    async (origin, destination,includeWaypoints = false) => {
      if (!origin || !destination) return null;
      if (useHereRouting) {
        try {
          const params = new URLSearchParams();
          params.append("transportMode", "car");
          params.append("origin", `${origin.lat},${origin.lng}`);
          params.append("destination", `${destination.lat},${destination.lng}`);
          ridewaypoints?.forEach(point => {
              params.append(
                  "via",
                  `${point.latitude},${point.longitude}`
              );
          });
          params.append("return", "polyline,summary");
          params.append("apiKey", hereApiKey);

          const avoid = [
            buildAvoidAreasParam(avoidPolygons),
            buildDangerAvoidParam(dangerZones),
          ]
            .filter(Boolean)
            .join("|");
          if (avoid) params.append("avoid[areas]", avoid);

          const res = await fetch(`${HERE_ROUTING_URL}?${params.toString()}`);
          const json = await res.json();
          if (!json?.routes?.length) {
            console.error("HERE routing returned no routes", json);
            return null;
          }

          const sections = json.routes[0].sections;
          const path = sections.flatMap((s) => decodeFlexiblePolyline(s.polyline));
          const durationSec = sections.reduce(
            (sum, s) => sum + (s.summary?.duration ?? 0),
            0
          );

          return { path, durationSec };
        } catch (e) {
          console.error("HERE routing failed", e);
          return null;
        }
      }

      // Google Directions (unchanged from before).
      if (!window.google?.maps) return null;
          const waypoints = (ridewaypoints || []).map(point => ({
        location: new window.google.maps.LatLng(
            Number(point.latitude),
            Number(point.longitude)
        ),
        stopover: true,
    }));

      return new Promise((resolve) => {
        const ds = new window.google.maps.DirectionsService();
        ds.route(
          {
            origin,
            destination,
            waypoints,
            optimizeWaypoints: false,
            travelMode: window.google.maps.TravelMode.DRIVING,
            provideRouteAlternatives: false,
          },
          (result, status) => {
            if (status === "OK") {
              const route = result.routes[0];
              const leg = route.legs[0];
              const path = [];
              // const path = route.overview_path.map((p) => ({
              //   lat: p.lat(),
              //   lng: p.lng(),
              // }));
              route.legs.forEach((leg) => {
                leg.steps.forEach((step) => {
                  step.path.forEach((point) => {
                    path.push({
                      lat: point.lat(),
                      lng: point.lng(),
                    });
                  });
                });
              });
              resolve({
                path,
                durationSec: route.legs.reduce(
                    (sum, leg) => sum + leg.duration.value,
                    0
                ),
              });
            } else {
              console.error("Google Directions failed:", status);
              resolve(null);
            }
          }
        );
      });
    },
    [isLoaded, pickupLatLng, dropLatLng, ridewaypoints, useHereRouting, dangerZones]
  );

  const fetchDirections = () => {
    if (!isLoaded || !pickupLatLng || !dropLatLng || !mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(pickupLatLng);
    rideDetails?.waypoints?.forEach(point => {
        bounds.extend({
            lat: Number(point.latitude),
            lng: Number(point.longitude),
        });
    });

    bounds.extend(dropLatLng);
    mapRef.current.fitBounds(bounds);
    mapRef.current.curvedPolyline?.setMap(null);
  };
  const fetchRoadRoute = async () => {
    if (!isLoaded || !pickupLatLng || !dropLatLng) return;
    const reqId = ++routeReqIdRef.current;
    const result = await routeBetween(pickupLatLng, dropLatLng);
    if (!result || reqId !== routeReqIdRef.current) return;

    setEtaText(formatDurationText(result.durationSec));
    const dropTime = new Date(Date.now() + result.durationSec * 1000);
    setDropTimeText(
      dropTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
    setRoadPath(result.path);

    const bounds = new window.google.maps.LatLngBounds();
    result.path.forEach((p) => bounds.extend(p));
    mapRef.current?.fitBounds(bounds);
  };

  const panWithOffset = (map, latLng, offsetY = 150) => {
    if (!map) return;
    const projection = map.getProjection();
    if (!projection) {
      window.google.maps.event.addListenerOnce(map, "projection_changed", () => {
        panWithOffset(map, latLng, offsetY);
      });
      return;
    }
    const zoom = map.getZoom();
    const scale = Math.pow(2, zoom);
    const worldPoint = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(latLng.lat, latLng.lng)
    );
    const pixelOffset = offsetY / scale;
    const newPoint = new window.google.maps.Point(
      worldPoint.x,
      worldPoint.y + pixelOffset
    );
    const newLatLng = projection.fromPointToLatLng(newPoint);
    map.panTo(newLatLng);
  };

  const updateLiveEta = async (driverLatLng) => {
    if (!isLoaded || !driverLatLng || !dropLatLng) return;
    const result = await routeBetween(driverLatLng, dropLatLng);
    if (!result) return;
    setEtaText(formatDurationText(result.durationSec));
    const dropTime = new Date(Date.now() + result.durationSec * 1000);
    setDropTimeText(
      dropTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const fetchDriverToDropRoute = async (driverLatLng) => {
    if (!isLoaded || !driverLatLng || !dropLatLng) return;
    const result = await routeBetween(driverLatLng, dropLatLng);
    if (!result) return;
    setDriverToDropPath(result.path);
  };

    useEffect(() => {
    if (!taxiCurrentLatLng || !dropLatLng) return;

    fetchDriverToDropRoute(taxiCurrentLatLng);
    }, [taxiCurrentLatLng, dropLatLng]);

    useEffect(() => {
    if (!taxiCurrentLatLng) return;

    const now = Date.now();
    if (now - lastEtaUpdateRef.current < 30000) return;

    lastEtaUpdateRef.current = now;
    updateLiveEta(taxiCurrentLatLng);
    }, [taxiCurrentLatLng]);

const waypoints =
  ridewaypoints?.map(point => ({
    location: {
      lat: Number(point.latitude),
      lng: Number(point.longitude),
    },
    stopover: true,
  })) || [];



useEffect(() => {
  if (isLoaded && pickupLatLng && dropLatLng) {
    fetchRoadRoute();
  }
}, [
  isLoaded,
  pickupLatLng,
  dropLatLng,
  ridewaypoints,
  useHereRouting,
  dangerZones
]);

    const fetchTaxiLocation = useCallback(async () => {
        try {
            const url = serverUrl + "/driver/fetch_tracking_lat_long";

            const obj = {
            driver_id: rideDetails?.driver_id,
            engagement_id: rideDetails?.engagement_id,
            };

            if (!obj.driver_id || !obj.engagement_id) return;

            const response = await makeGetRequestToUrl(url, obj);
            setDriverlocation(response.data[0]);

            if (response.flag === 414) {
                setRideEndCheck(true);

                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }

            if (response.flag === 200 && response.data?.length) {
            const newLatLng = {
                lat: Number(response.data[0].latitude),
                lng: Number(response.data[0].longitude),
            };
            setRideWaypoints(prev => {
              const next = response.data[0]?.waypoints || [];
              return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
            });
            setTaxiCurrentLatLng(prevCurrent => {
            if (!prevCurrent) {
                setTaxiPreviousLatLng(newLatLng);
                return newLatLng;
            }
            setTaxiPreviousLatLng(prevCurrent);
            return newLatLng;
        });
      }
    } catch (e) {
      console.error(e);
    }
    }, [rideDetails?.driver_id, rideDetails?.engagement_id,serverUrl]);


    useEffect(() => {
  if (!rideDetails) return;

  setPickupLatLng({
    lat: Number(rideDetails.pickup_latitude),
    lng: Number(rideDetails.pickup_longitude),
  });

  setDropLatLng({
    lat: Number(rideDetails.drop_latitude),
    lng: Number(rideDetails.drop_longitude),
  });
}, [rideDetails]);

    useEffect(() => {
  if (isLoaded && pickupLatLng && dropLatLng) {
    fetchDirections();
  }
}, [isLoaded, pickupLatLng, dropLatLng]);

    useEffect(() => {
    if (!rideDetails?.driver_id || !rideDetails?.engagement_id) return;

    fetchTaxiLocation();

    pollingIntervalRef.current = setInterval(
        fetchTaxiLocation,
        POLL_INTERVAL
    );

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    }, [
    rideDetails?.driver_id,
    rideDetails?.engagement_id,
    fetchTaxiLocation
    ]);


    useEffect(() => {
      const fetchDangerZones = async () => {
        // only matters when HERE (safest route) is driving the polyline
        if (rideDetails?.is_safest_route !== 1) return;
        if (!rideDetails?.pickup_latitude || !rideDetails?.pickup_longitude) return;

        try {
          const res = await makeGetRequestToUrl(
            `${config.autosUrl}/get_danger_zones`,   // swap to your taxi base URL if different
            {
              is_panel_request: 1,
              driver_id: rideDetails?.driver_id,
              pickup_latitude: rideDetails.pickup_latitude,
              pickup_longitude: rideDetails.pickup_longitude,
            }
          );
          console.log(res.data, "danger zones"); // confirm the shape once, then you can remove
          if (res.flag === 200 && Array.isArray(res.data)) {
            setDangerZones(res.data);
          }
        } catch (e) {
          console.error("Failed to fetch danger zones", e);
        }
      };
      fetchDangerZones();
    }, [
      rideDetails?.is_safest_route,
      rideDetails?.pickup_latitude,
      rideDetails?.pickup_longitude,
      rideDetails?.user_id,
    ]);




  useEffect(() => {
    if (!taxiMarker || !taxiPreviousLatLng || !taxiCurrentLatLng) return;

    if (
      taxiPreviousLatLng.lat === taxiCurrentLatLng.lat &&
      taxiPreviousLatLng.lng === taxiCurrentLatLng.lng
        ) return;

    const startPos = new window.google.maps.LatLng(
      taxiPreviousLatLng.lat,
      taxiPreviousLatLng.lng
    );

    const endPos = new window.google.maps.LatLng(
      taxiCurrentLatLng.lat,
      taxiCurrentLatLng.lng
    );

        // ⏱ FIXED DURATION = API INTERVAL
        const DURATION = 6000; // ms

    let startTime = null;
    let frameId;

    function calculateBearing(start, end) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const toDeg = (rad) => (rad * 180) / Math.PI;
      const lat1 = toRad(start.lat);
      const lat2 = toRad(end.lat);
      const dLng = toRad(end.lng - start.lng);
      const y = Math.sin(dLng) * Math.cos(lat2);
      const x =
        Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
      return (toDeg(Math.atan2(y, x)) + 360) % 360;
    }

        function animate(timestamp) {
            if (!startTime) startTime = timestamp;

            const elapsed = timestamp - startTime;
            const t = Math.min(elapsed / DURATION, 1);

            const pos = window.google.maps.geometry.spherical.interpolate(
                startPos,
                endPos,
                t
            );

            taxiMarker.setPosition(pos);

            // 🔄 Rotate towards destination
            const bearing = calculateBearing(
                taxiPreviousLatLng,
                taxiCurrentLatLng
            );

            taxiMarker.setIcon({
                ...taxiMarker.getIcon(),
                rotation: bearing,
            });

            if (mapRef.current && followTaxiRef.current && elapsed % 300 < 16) {
                panWithOffset(mapRef.current, pos.toJSON(), 160);
            }

            if (t < 1) {
                frameId = requestAnimationFrame(animate);
            }
        }

        frameId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frameId);

  }, [taxiPreviousLatLng, taxiCurrentLatLng, taxiMarker]);

  useEffect(() => {
    if (taxiMarker) taxiMarker.setMap(null);

    setTaxiMarker(null);
    setTaxiPreviousLatLng(null);
    setTaxiCurrentLatLng(null);
  }, [rideDetails]);


  useEffect(() => {
    if (!isLoaded || !mapRef.current || !taxiCurrentLatLng) return;
    const carSymbol = {
      path: `M17.402,0H5.643C2.526,0,0,3.467,0,6.584v34.804c0,3.116,2.526,5.644,5.643,5.644h11.759c3.116,0,5.644-2.527,5.644-5.644 V6.584C23.044,3.467,20.518,0,17.402,0z M22.057,14.188v11.665l-2.729,0.351v-4.806L22.057,14.188z M20.625,10.773 c-1.016,3.9-2.219,8.51-2.219,8.51H4.638l-2.222-8.51C2.417,10.773,11.3,7.755,20.625,10.773z M3.748,21.713v4.492l-2.73-0.349 V14.502L3.748,21.713z M1.018,37.938V27.579l2.73,0.343v8.196L1.018,37.938z M2.575,40.882l2.218-3.336h13.771l2.219,3.336H2.575z M19.328,35.805v-7.872l2.729-0.355v10.048L19.328,35.805z`,
      fillColor: "#91BDFF",
      fillOpacity: 1,
      strokeColor: "#1E2839",
      strokeWeight: 1,
      scale: isMobile ? 1.3 : 1,
      anchor: new window.google.maps.Point(12, 12),
      rotation: 0,
    };

    if (!taxiMarker) {
      const marker = new window.google.maps.Marker({
        position: taxiCurrentLatLng,
        map: mapRef.current,
        icon: carSymbol,
      });
      setTaxiMarker(marker);
    } else {
      taxiMarker.setPosition(taxiCurrentLatLng);
    }
  }, [isLoaded, taxiCurrentLatLng]);

  useEffect(() => {
    if (!currentLatLng) return;

    const poll = () => {
      setPreviousLatLng(currentLatLng);

            const next = getNextDummyLocation(
                currentLatLng.lat,
                currentLatLng.lng
            );

      setCurrentLatLng(next);
    };

    const interval = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [currentLatLng]);

  useEffect(() => {
    if (!infoWindowRef.current || !mapRef.current) return;

        // re-bind content when API data arrives
    if (mapRef.current.pickupMarker) {
      mapRef.current.pickupMarker.addListener("click", () => {
        infoWindowRef.current.setContent(`
          <div style="font-size:13px;max-width:220px">
            <strong>Pickup Location</strong><br/>
            ${rideDetails?.pickup_address || ""}
          </div>
        `);
        infoWindowRef.current.open(mapRef.current, mapRef.current.pickupMarker);
      });
    }

    if (mapRef.current.dropMarker) {
      mapRef.current.dropMarker.addListener("click", () => {
        infoWindowRef.current.setContent(`
          <div style="font-size:13px;max-width:220px">
            <strong>Drop Location</strong><br/>
            ${rideDetails?.drop_address || ""}
          </div>
        `);
        infoWindowRef.current.open(mapRef.current, mapRef.current.dropMarker);
      });
    }
  }, [rideDetails]);



  useEffect(() => {
    if (!markerRef.current || !previousLatLng || !currentLatLng) return;

    const start = new window.google.maps.LatLng(
      previousLatLng.lat,
      previousLatLng.lng
    );

    const end = new window.google.maps.LatLng(
      currentLatLng.lat,
      currentLatLng.lng
    );

    const distance =
      window.google.maps.geometry.spherical.computeDistanceBetween(start, end);

    const duration = Math.max(2000, (distance / 5) * 1000);
    let startTime = null;

    const animate = (time) => {
      if (!startTime) startTime = time;

      const progress = Math.min((time - startTime) / duration, 1);

      const pos = window.google.maps.geometry.spherical.interpolate(
        start,
        end,
        progress
      );

      markerRef.current.setPosition(pos);
            panWithOffset(mapRef.current, pos.toJSON(), 160); // 120–180 is ideal


            if (progress < 1) {
                requestAnimationFrame(animate);
            }
    };

    requestAnimationFrame(animate);
  }, [currentLatLng]);
    // useEffect(() => {
    //     if (!mapRef.current || !taxiCurrentLatLng) return;

    //     mapRef.current.panTo(taxiCurrentLatLng);
    // }, [taxiCurrentLatLng]);


    const onMapLoad = (map) => {
    mapRef.current = map;

    map.setOptions({
        padding: {
        top: 70,
        bottom: 220,
        left: 20,
        right: 20,
        },
    });

    // ✅ pickup marker
    mapRef.current.pickupMarker = new window.google.maps.Marker({
        map,
        zIndex: 5,
        icon: {
        url: pickupIcon,
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32),
      },
    });

    {rideDetails?.waypoints?.map((point, index) => (
      <Marker
      key={point.stop_id}
      position={{
        lat: Number(point.latitude),
                lng: Number(point.longitude),
              }}
            label={`${index + 1}`}
        />
    ))}

      // ✅ drop marker
      mapRef.current.dropMarker = new window.google.maps.Marker({
        map,
        zIndex: 5,
        icon: {
          url: dropIcon,
          scaledSize: new window.google.maps.Size(32, 32),
          anchor: new window.google.maps.Point(16, 32),
        },
      });
    infoWindowRef.current = new window.google.maps.InfoWindow();

    mapRef.current.pickupMarker.addListener("click", () => {
      infoWindowRef.current.setContent(`
        <div style="font-size:13px;max-width:220px">
          <strong>Pickup Location</strong><br/>
          ${rideDetails?.pickup_address || ""}
        </div>
      `);
      infoWindowRef.current.open(map, mapRef.current.pickupMarker);
    });

    mapRef.current.dropMarker.addListener("click", () => {
      infoWindowRef.current.setContent(`
        <div style="font-size:13px;max-width:220px">
          <strong>Drop Location</strong><br/>
          ${rideDetails?.drop_address || ""}
        </div>
      `);
      infoWindowRef.current.open(map, mapRef.current.dropMarker);
    });

    // 🔥 CRITICAL FIX
    if (pickupLatLng) {
        mapRef.current.pickupMarker.setPosition(pickupLatLng);
    }
    if (dropLatLng) {
        mapRef.current.dropMarker.setPosition(dropLatLng);
    }
  };

    useEffect(() => {
    if (
      !mapRef.current ||
      !mapRef.current.pickupMarker ||
      !mapRef.current.dropMarker ||
      !pickupLatLng ||
      !dropLatLng
    ) return;

    mapRef.current.pickupMarker.setPosition(pickupLatLng);
    mapRef.current.dropMarker.setPosition(dropLatLng);
  }, [pickupLatLng, dropLatLng]);

    if (!isLoaded) {
        return <div>Loading map…</div>;
    }

    if (rideEndCheck && rideDetails) {
    return (
        <>
        <Navbar logo={logo}/>

        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#b9b9b9ff",
            px: 2,
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 420, md: 520 },
              background: "#fff",
              borderRadius: 3,
              p: 2.5,
              boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
            }}
          >
            {/* ===== HEADER ===== */}
            <Box textAlign="center" mb={2}>
              <Typography fontWeight={600} fontSize={18}>
                Ride Completed ✅
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thank you for riding with us
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* ===== DRIVER + VEHICLE (YOUR LAYOUT) ===== */}
            <Box
                sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                }}
            >
                {/* LEFT: Driver */}
              <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                <Avatar
                    src={rideDetails?.driver_image || altProfile}
                    sx={{ width: 40, height: 40 }}
                />

                <Box>
                    <Typography fontWeight={600}>
                    {rideDetails?.driver_name}
                    </Typography>

                    {rideDetails?.rating && (
                    <Box
                        display="inline-flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{
                        backgroundColor: "#F7F7F7",
                        px: 0.75,
                        py: 0.25,
                        borderRadius: "6px",
                        mt: 0.5,
                        }}
                    >
                        <StarIcon sx={{ fontSize: 14, color: "#FFC107" }} />
                        <Typography variant="caption">
                        {rideDetails.rating.toFixed(1)}
                        </Typography>
                    </Box>
                    )}
                </Box>
                </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 36 }} />

                {/* RIGHT: Vehicle */}
              <Box display="flex" alignItems="center" gap={1}>
                {rideDetails?.vehicle_type === 1 && (
                  <img src={vehicleTypeCar} alt="car" height={36} />
                )}
                {rideDetails?.vehicle_type === 2 && (
                  <img src={vehicleTypeBike} alt="bike" height={36} />
                )}
                {rideDetails?.vehicle_type === 3 && (
                  <img src={vehicletypeAuto} alt="auto" height={36} />
                )}
                <Typography variant="body2" fontWeight={500}>
                  {rideDetails?.vehicle_no}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* ===== LOCATIONS (YOUR GRID STYLE) ===== */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 3,
                rowGap: 0.75,
              }}
            >
                {/* Pickup label */}
              <Box display="flex" alignItems="center" gap={1}>
                <img src={Pickupicon} alt="Pickup" width={18} />
                <Typography fontWeight={500} color="text.secondary">
                  Pickup Location
                </Typography>
              </Box>

                {/* Drop label */}
              <Box display="flex" alignItems="center" gap={1}>
                <img src={Dropicon} alt="Drop" width={18} />
                <Typography fontWeight={500} color="text.secondary">
                  Drop Location
                </Typography>
              </Box>

                {/* Pickup address */}
              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: "1.4em",
                  pl: "26px",
                }}
              >
                {rideDetails?.pickup_address}
              </Typography>

                {/* Drop address */}
              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: "1.4em",
                  pl: "26px",
                }}
              >
                {rideDetails?.drop_address}
              </Typography>
            </Box>
            {driverLocation?.waypoints?.length > 0 &&(
                  <Box sx={{ mt: 2 }}>
                      <Typography
                          fontWeight={500}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                      >
                          Stop Locations :
                      </Typography>

                      {driverLocation?.waypoints?.map((stop, index) => (
                          <Box
                              key={stop.stop_id || index}
                              sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1.5,
                                  mb: 1.5,
                              }}
                          >
                              <Box
                                  sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      bgcolor: "#1976d2",
                                      color: "#fff",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      mt: "2px",
                                  }}
                              >
                                  {index + 1}
                              </Box>

                              <Typography
                                  variant="body2"
                                  sx={{
                                      lineHeight: "1.4em",
                                      wordBreak: "break-word",
                                  }}
                              >
                                  {stop.address}
                              </Typography>
                          </Box>
                      ))}
                  </Box>
                  )}
          </Box>
        </Box>
      </>
    );
  }


    return (
        <>
            <Navbar logo={logo} />
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Top Gap */}
                <Box sx={{ height: 12 }} />

                {/* Map Container */}
                <Box
                    sx={{
                        flex: 1,
                        mt: isMobile ? 4 : 9,
                        mx: isMobile ? 0 : 2,
                        mb: isMobile ? 0 : 2,
                        borderRadius: 4,
                        overflow: "hidden",
                        backgroundColor: "#f7f7f7",
                    }}
                >
                    {pickupLatLng && 
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        // center={taxiCurrentLatLng || pickupLatLng}
                        zoom={15}
                        onLoad={onMapLoad}
                        options={{
                            gestureHandling: "greedy",
                            zoomControl: true,
                            streetViewControl: false,
                            fullscreenControl: false,
                            mapTypeControl: false,
                        }}
                        style={{
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {roadPath.length > 0 && (
                        <Polyline
                            path={roadPath}
                            options={{
                            strokeColor: "#0065FF",
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                            geodesic: false,
                            }}
                        />
                        )}
                        {/* {driverToDropPath.length > 0 && (
                        <Polyline
                            path={driverToDropPath}
                            options={{
                            strokeColor: "#0065FF",
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                            }}
                        />
                        )} */}
                          {ridewaypoints?.map((point, index) => (
                            <Marker
                              key={point.stop_id}
                              position={{
                                lat: Number(point.latitude),
                                lng: Number(point.longitude),
                              }}
                              label={{
                                text: `${index + 1}`,
                                color: "#fff",
                              }}
                            />
                          ))}
                          {dangerZones?.map((zone) =>
                            zone.geofence?.map((ring, i) => (
                              <Polygon
                                key={`${zone.zone_id}-${i}`}
                                paths={ring.map((pt) => ({ lat: pt.lat, lng: pt.lng }))}
                                options={{
                                  strokeColor: "#FF0000",
                                  strokeOpacity: 0.9,
                                  strokeWeight: 2,
                                  fillColor: "#FF0000",
                                  fillOpacity: 0.15,
                                  clickable: false,
                                  zIndex: 1,
                                }}
                              />
                            ))
                          )}



                    </GoogleMap>
                    }
                </Box>
            </Box>
            <Accordion
                defaultExpanded={false}
                sx={{
                    position: "absolute",
                    bottom: isMobile ? 0 : 20,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: { xs: "100%", sm: 420, md: 520, lg: 620 },
                    background: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                    zIndex: 10,
                    overflow: "hidden",
                    "&:before": { display: "none" }, 
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        px: 2,
                        mt: 1,
                        minHeight: "unset",
                        "& .MuiAccordionSummary-content": {
                            margin: 0,
                        },
                    }}
                >
                    <Box>
                        {rideEndCheck ? <Typography fontWeight={600}>
                            Ride Completed
                        </Typography> : <Typography fontWeight={600}>
                            Ongoing Ride
                        </Typography>}
                        <Typography color="text.secondary">
                            {etaText && dropTimeText
                                ? `${etaText} away · Drop-off by ${dropTimeText}`
                                : "Calculating ETA..."}
                        </Typography>

                    </Box>
                </AccordionSummary>


                <AccordionDetails sx={{ p: 2, pt: 0 }}>
                    <Divider sx={{ my: 1 }} />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection:"row",
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: { xs: 1.5, sm: 2 },
                        }}
                    >
                        {/* LEFT: Driver info */}
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            sx={{
                                width: { xs: "50%", sm: "auto" },
                                mr: { xs: 0, sm: 22 },
                            }}
                         >

                            <Avatar
                                src={rideDetails?.driver_image || altProfile}
                                sx={{ width: 40, height: 40 }}
                            />

                            <Box>
                                <Typography fontWeight={600}>
                                    {rideDetails?.driver_name}
                                </Typography>

                                <Box
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={0.5}
                                    sx={{
                                        backgroundColor: "#F7F7F7",
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: "6px",
                                        mt: 0.5,
                                    }}
                                >
                                    <StarIcon sx={{ fontSize: 14, color: "#FFC107" }} />
                                    <Typography variant="caption">
                                        {rideDetails?.rating?.toFixed(1)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* CENTER DIVIDER */}
                        <Divider orientation="vertical" flexItem sx={{ height: 40 }} />

                        {/* RIGHT: Vehicle info */}
                        <Box display="flex" alignItems="center" gap={1}>
                            {rideDetails?.vehicle_type === 1 && (
                                <img src={vehicleTypeCar} alt="car" height={40} />
                            )}
                            {rideDetails?.vehicle_type === 2 && (
                                <img src={vehicleTypeBike} alt="bike" height={40} />
                            )}
                            {rideDetails?.vehicle_type === 3 && (
                                <img src={vehicletypeAuto} alt="auto" height={40} />
                            )}
                            <Typography variant="body2" fontWeight={500}>
                                {rideDetails?.vehicle_no}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* ===== LOCATIONS ===== */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            columnGap: 3,
                            rowGap: 0.5,
                        }}
                    >
                        {/* Pickup Label */}
                        <Box display="flex" alignItems="center" gap={1}>
                            <img src={Pickupicon} alt="Pickup" width={18} />
                            <Typography fontWeight={500} color="text.secondary">
                                Pickup Location :
                            </Typography>
                        </Box>

                        {/* Drop Label */}
                        <Box display="flex" alignItems="center" gap={1}>
                            <img src={Dropicon} alt="Drop" width={18} />
                            <Typography fontWeight={500} color="text.secondary">
                                Drop Location :
                            </Typography>
                        </Box>

                        {/* Pickup Address */}
                        <Typography
                            variant="body2"
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: "1.3em",
                                maxHeight: "5.2em",
                                pl: "26px",
                            }}
                        >
                            {rideDetails?.pickup_address}
                        </Typography>

                        {/* Drop Address */}
                        <Typography
                            variant="body2"
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: "1.3em",
                                maxHeight: "5.2em",
                                pl: "26px",
                            }}
                        >
                            {rideDetails?.drop_address}
                        </Typography>
                    </Box>

              {ridewaypoints?.length > 0 &&(
                  <Box sx={{ mt: 2 }}>
                      <Typography
                          fontWeight={500}
                          color="text.secondary"
                          sx={{ mb: 1 }}
                      >
                          Stop Locations :
                      </Typography>

                      {ridewaypoints?.map((stop, index) => (
                          <Box
                              key={stop.stop_id || index}
                              sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1.5,
                                  mb: 1.5,
                              }}
                          >
                              <Box
                                  sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      bgcolor: "#1976d2",
                                      color: "#fff",
                                      fontSize: 12,
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      mt: "2px",
                                  }}
                              >
                                  {index + 1}
                              </Box>

                              <Typography
                                  variant="body2"
                                  sx={{
                                      lineHeight: "1.4em",
                                      wordBreak: "break-word",
                                  }}
                              >
                                  {stop.address}
                              </Typography>
                          </Box>
                      ))}
                  </Box>
                  )}
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default MapViewPublic;

